/**
 * Framework-free helpers: detect magic-link / login-code / session-key creds and build `/auth?...`.
 * Use RAW path+query (not Vue `route.query` / `URLSearchParams` for *parsing*) so `+` in emails is not turned into space.
 * Query keys: email (optional), loginCode, token, sessionKey; precedence on /auth: sessionKey > loginCode > token.
 */

import { parseQueryPreservingPlus, splitUrlOnce } from '@common/core-layer/utils/url-query-parse.utils'
import { isCoursesAdminPreviewCredentialFromParts } from '@courses/admin-preview-layer/utils/courses-admin-preview-url.utils'

import { resolveMagicLinkRedirectUrlParam } from '@plugins/utils/magic-link-redirect-url'

const MAGIC_LINK_QUERY_KEYS = ['email', 'loginCode', 'token', 'sessionKey'] as const

/** Stripped from the post-auth URL together with magic-link credentials. */
const MAGIC_LINK_STRIP_QUERY_KEYS = [...MAGIC_LINK_QUERY_KEYS, 'redirectUrl'] as const

export const AUTH_PATH = '/auth'

const LOGIN_PATH = '/login'

/**
 * Paths where `token` / `loginCode` are not magic-link auth creds
 * (password reset, invite / set-new-password, etc.) — `sessionKey` still owns login.
 */
const MAGIC_LINK_SKIP_PATHS = new Set<string>(['/reset-password', '/set-password'])

function buildCleanUrl(pathname: string, queryParams: Record<string, string>): string {
  const cleaned = { ...queryParams }
  for (const key of MAGIC_LINK_STRIP_QUERY_KEYS) {
    delete cleaned[key]
  }
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(cleaned)) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, value)
    }
  }
  const queryString = search.toString()
  return queryString ? `${pathname}?${queryString}` : pathname
}

export interface MagicLinkPayload {
  email: string | undefined
  loginCode: string | undefined
  token: string | undefined
  sessionKey: string | undefined
  hasLoginCode: boolean
  hasToken: boolean
  hasSessionKey: boolean
  cleanUrl: string
}

export function isMagicLinkSkipPath(pathname: string): boolean {
  return MAGIC_LINK_SKIP_PATHS.has(pathname)
}

export function getMagicLinkPayload(pathname: string, queryString: string): MagicLinkPayload | null {
  // Courses admin preview's `token` (+ `is_preview=true` + `location_id`) is an agency Bearer,
  // not a magic-link cred — owned by admin-preview middleware. Bail so neither this plugin nor
  // `getMagicLinkAuthUrl` (auth.global) hijacks it to /auth.
  if (isCoursesAdminPreviewCredentialFromParts(pathname, queryString)) return null

  const queryParams = parseQueryPreservingPlus(queryString)
  const email = queryParams.email?.trim() || undefined
  const loginCode = queryParams.loginCode
  const token = queryParams.token
  const sessionKey = queryParams.sessionKey
  const hasSessionKey = !!sessionKey?.trim()
  const shouldSkipTokenCredentials = isMagicLinkSkipPath(pathname)
  const hasLoginCode = !shouldSkipTokenCredentials && !!loginCode?.trim()
  const hasToken = !shouldSkipTokenCredentials && !!token?.trim()
  if (shouldSkipTokenCredentials && !hasSessionKey) return null
  if (!hasLoginCode && !hasToken && !hasSessionKey) return null

  const redirectTarget = resolveMagicLinkRedirectUrlParam(queryParams.redirectUrl)
  const destination = redirectTarget ?? pathname

  return {
    email,
    loginCode: hasLoginCode ? loginCode : undefined,
    token: hasToken ? token : undefined,
    sessionKey,
    hasLoginCode,
    hasToken,
    hasSessionKey,
    cleanUrl: buildCleanUrl(destination, queryParams),
  }
}

/** `/auth?...` with creds; optional `redirect` = pre-strip original URL for post-login navigation. */
export function buildAuthUrl(payload: MagicLinkPayload): string {
  const search = new URLSearchParams()
  if (payload.email) search.set('email', payload.email)
  if (payload.hasSessionKey && payload.sessionKey) search.set('sessionKey', payload.sessionKey)
  if (payload.hasLoginCode && payload.loginCode) search.set('loginCode', payload.loginCode)
  if (payload.hasToken && payload.token) search.set('token', payload.token)
  if (payload.cleanUrl && payload.cleanUrl !== '/') search.set('redirect', payload.cleanUrl)
  return `${AUTH_PATH}?${search.toString()}`
}

/**
 * Resolves the same payload as `getMagicLinkAuthUrl` would use: top-level creds, or creds nested
 * inside `/login?redirect=...`. Returns null on `/auth` (caller should no-op) or when no creds.
 */
export function getMagicLinkPayloadFromFullPath(rawUrl: string): MagicLinkPayload | null {
  const [path, queryString] = splitUrlOnce(rawUrl)
  /** Do not treat `/auth` as a cred recovery entry (avoids loop with `getMagicLinkAuthUrl`). */
  if (path === AUTH_PATH) return null

  const direct = getMagicLinkPayload(path, queryString)
  if (direct) return direct

  if (path === LOGIN_PATH && queryString) {
    const params = parseQueryPreservingPlus(queryString)
    const redirect = params.redirect
    if (redirect) {
      const [innerPath, innerQuery] = splitUrlOnce(redirect)
      return getMagicLinkPayload(innerPath || '/', innerQuery)
    }
  }

  return null
}

export function getMagicLinkAuthUrl(rawUrl: string): string | null {
  if (splitUrlOnce(rawUrl)[0] === AUTH_PATH) return null
  const payload = getMagicLinkPayloadFromFullPath(rawUrl)
  if (!payload) return null
  return buildAuthUrl(payload)
}
